import { useState, useEffect } from 'react';
import { diagnosePWAInstallability, logPWADiagnostics } from '../utils/pwaDebug';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export const usePWAInstall = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [canShowButton, setCanShowButton] = useState(false);

  useEffect(() => {
    // Check if app is already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      setCanShowButton(true);
      return;
    }

    // Check if running on iOS
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
    if (isIOS) {
      setIsInstallable(true);
      setCanShowButton(true);
      return;
    }

    // For other platforms, check if service worker is registered
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then(() => {
        setCanShowButton(true);
        // Run diagnostics when service worker is ready
        diagnosePWAInstallability().then(diagnostics => {
          logPWADiagnostics(diagnostics);
          if (diagnostics.meetsCriteria) {
            // All criteria met - mark as installable even if event hasn't fired
            // The browser may fire the event later, but we can show the button now
            setIsInstallable(true);
            console.log('[PWA] All criteria met — app is installable');
            console.log('[PWA] Install button will work once the browser fires beforeinstallprompt');
            console.log('[PWA] User engagement may be required before the browser shows the prompt');
          } else {
            console.warn('[PWA] App does not meet all criteria for automatic install prompt');
            console.warn('[PWA] Browser may not show the install prompt until all criteria are met');
          }
        });
      }).catch(() => {
        setCanShowButton(true);
      });
    } else {
      // Even without service worker, show button (will show instructions)
      setCanShowButton(true);
    }

    // Listen for the beforeinstallprompt event (Chrome/Edge)
    // This event ONLY fires when:
    // 1. All PWA criteria are met
    // 2. User has engaged with the site (visited multiple times, spent time)
    // 3. App is not already installed
    // 4. Browser decides it's appropriate to show install prompt
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setIsInstallable(true);
      setCanShowButton(true);
      console.log('[PWA] beforeinstallprompt fired — install prompt is available');

      // Run diagnostics to confirm everything is working
      diagnosePWAInstallability().then(diagnostics => {
        if (diagnostics.meetsCriteria) {
          console.log('[PWA] All criteria met — install prompt should work');
        }
      });
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    
    // Log why the prompt might not appear
    console.log('[PWA] Waiting for beforeinstallprompt event');
    console.log('[PWA] This event only fires when:');
    console.log('   1. All PWA criteria are met (manifest, service worker, HTTPS, icons) [ready when ok]');
    console.log('   2. User has engaged with the site (visited multiple times) [pending]');
    console.log('   3. Browser determines it is appropriate to show the prompt [pending]');
    console.log('   The install button will work once this event fires, or show instructions if it does not.');
    console.log('   Tip: visit the site a few times and spend some time on it to trigger the prompt.');

    // Check if app was just installed
    window.addEventListener('appinstalled', () => {
      setIsInstalled(true);
      setIsInstallable(false);
      setDeferredPrompt(null);
      console.log('[PWA] App was installed');
    });

    // Also check periodically if install prompt becomes available
    const checkInterval = setInterval(() => {
      if (!isInstalled && !deferredPrompt) {
        // Re-check if we can show the button
        if ('serviceWorker' in navigator) {
          navigator.serviceWorker.ready.then(() => {
            setCanShowButton(true);
          });
        }
      }
    }, 5000);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      clearInterval(checkInterval);
    };
  }, [deferredPrompt, isInstalled]);

  // Helper function to check PWA readiness
  const checkPWAReadiness = async (): Promise<{
    serviceWorkerRegistered: boolean;
    manifestAccessible: boolean;
    isHTTPS: boolean;
  }> => {
    let serviceWorkerRegistered = false;
    let manifestAccessible = false;
    const isHTTPS = window.location.protocol === 'https:' || window.location.hostname === 'localhost';

    // Check service worker
    if ('serviceWorker' in navigator) {
      try {
        const registration = await navigator.serviceWorker.ready;
        serviceWorkerRegistered = !!registration;
      } catch (error) {
        console.error('Service worker check failed:', error);
      }
    }

    // Check manifest
    try {
      const response = await fetch('/site.webmanifest');
      manifestAccessible = response.ok;
    } catch (error) {
      console.error('Manifest check failed:', error);
    }

    return {
      serviceWorkerRegistered,
      manifestAccessible,
      isHTTPS,
    };
  };

  const handleInstall = async (): Promise<{ success: boolean; message?: string; readyButWaiting?: boolean }> => {
    if (isInstalled) {
      return { success: false, message: 'App is already installed' };
    }

    // iOS - show instructions
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
    if (isIOS) {
      return { success: false, message: 'ios_instructions' };
    }

    // Check if we have a deferred prompt (Chrome/Edge)
    if (deferredPrompt) {
      try {
        await deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        
        if (outcome === 'accepted') {
          setIsInstallable(false);
          setDeferredPrompt(null);
          return { success: true };
        } else {
          return { success: false, message: 'User dismissed the install prompt' };
        }
      } catch (error) {
        console.error('Error showing install prompt:', error);
        return { success: false, message: 'Failed to show install prompt' };
      }
    }

    // No deferred prompt available - check PWA criteria
    const diagnostics = await checkPWAReadiness();
    const fullDiagnostics = await diagnosePWAInstallability();
    console.log('[PWA] Install diagnostics:', diagnostics);
    console.log('[PWA] Full diagnostics:', fullDiagnostics);
    
    if (!diagnostics.serviceWorkerRegistered) {
      return { success: false, message: 'Service worker not registered. Please refresh the page.' };
    }
    
    if (!diagnostics.manifestAccessible) {
      return { success: false, message: 'Manifest file not accessible. Please check the manifest configuration.' };
    }

    // If all criteria are met but no prompt, the browser hasn't fired the event yet
    // This is normal - browsers require user engagement before showing the prompt
    if (fullDiagnostics.meetsCriteria) {
      return { 
        success: false, 
        message: 'install_instructions_ready',
        // Add flag to indicate we're ready but waiting for browser
        readyButWaiting: true
      };
    }

    // If all criteria are met but no prompt, provide instructions
    return { success: false, message: 'install_instructions' };
  };

  return {
    isInstallable,
    isInstalled,
    canShowButton,
    handleInstall,
    deferredPrompt: !!deferredPrompt, // Expose whether we have a prompt available
  };
};
